import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { PortalShell } from "../components/PortalShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ApiError } from "../lib/api";
import {
  ALLOWED_SCOPES,
  createApiKey,
  listApiKeys,
  revokeApiKey,
  type ApiKey,
  type CreatedApiKey,
} from "../lib/apiKeys";

export function ApiKeysPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["api-keys"],
    queryFn: listApiKeys,
  });

  const [showForm, setShowForm] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const keys = Array.isArray(data) ? data : [];

  const revokeMutation = useMutation({
    mutationFn: revokeApiKey,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["api-keys"] }),
  });

  return (
    <PortalShell>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-neutral-900">API keys</h1>
          <p className="text-sm text-neutral-600">
            Server-to-server access keys. Treat them like passwords.
          </p>
        </div>
        {!showForm && createdKey === null && (
          <Button variant="primary" onClick={() => setShowForm(true)}>
            + New API key
          </Button>
        )}
      </header>

      {createdKey !== null && (
        <CreatedKeyBanner
          created={createdKey}
          onClose={() => {
            setCreatedKey(null);
            setShowForm(false);
            void queryClient.invalidateQueries({ queryKey: ["api-keys"] });
          }}
        />
      )}

      {showForm && createdKey === null && (
        <CreateKeyForm onCreated={(k) => setCreatedKey(k)} onCancel={() => setShowForm(false)} />
      )}

      <section className="mt-6 rounded-lg border border-neutral-200 bg-white shadow-sm">
        {isLoading ? (
          <p className="px-6 py-12 text-sm text-neutral-500">Loading…</p>
        ) : error instanceof ApiError ? (
          <p
            role="alert"
            className="m-6 rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
          >
            {error.problem.detail || error.problem.title}
          </p>
        ) : keys.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-neutral-500">
            No API keys yet. Create one to start automating.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-200">
            {keys.map((key) => (
              <ApiKeyRow
                key={key.id}
                apiKey={key}
                onRevoke={() => revokeMutation.mutate(key.id)}
                revoking={revokeMutation.isPending && revokeMutation.variables === key.id}
              />
            ))}
          </ul>
        )}
      </section>
    </PortalShell>
  );
}

function ApiKeyRow({
  apiKey,
  onRevoke,
  revoking,
}: {
  apiKey: ApiKey;
  onRevoke: () => void;
  revoking: boolean;
}): JSX.Element {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-neutral-900">{apiKey.name}</p>
        <p className="mt-1 flex flex-wrap gap-1.5 text-xs text-neutral-500">
          {apiKey.scopes.map((scope) => (
            <span
              key={scope}
              className="rounded-full bg-neutral-100 px-2 py-0.5 font-mono text-[11px] text-neutral-700"
            >
              {scope}
            </span>
          ))}
        </p>
        <p className="mt-1 text-xs text-neutral-500">
          Created {formatDate(apiKey.created_at)}
          {apiKey.last_used_at !== null && (
            <> · Last used {formatDate(apiKey.last_used_at)}</>
          )}
          {apiKey.expires_at !== null && (
            <> · Expires {formatDate(apiKey.expires_at)}</>
          )}
        </p>
      </div>
      <Button
        variant="destructive"
        size="sm"
        onClick={() => {
          if (window.confirm(`Revoke "${apiKey.name}"? This cannot be undone.`)) {
            onRevoke();
          }
        }}
        loading={revoking}
      >
        Revoke
      </Button>
    </li>
  );
}

function formatDate(iso: string | null): string {
  if (iso === null) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function CreateKeyForm({
  onCreated,
  onCancel,
}: {
  onCreated: (key: CreatedApiKey) => void;
  onCancel: () => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>(["templates.read"]);
  const [expiresIn, setExpiresIn] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: createApiKey,
    onSuccess: onCreated,
  });

  function toggleScope(scope: string): void {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (scopes.length === 0) {
      setError("Pick at least one scope.");
      return;
    }
    mutation.mutate(
      {
        name,
        scopes,
        expires_in_days: expiresIn === "" ? undefined : Number(expiresIn),
      },
      {
        onError: (err) => {
          if (err instanceof ApiError) {
            setError(err.problem.detail || err.problem.title);
          } else {
            setError("Couldn't create key. Please try again.");
          }
        },
      },
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-neutral-900">New API key</h2>
      <Input
        label="Name"
        required
        maxLength={64}
        hint="Shown in audit logs and the API keys list."
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <fieldset>
        <legend className="text-sm font-medium text-neutral-700">Scopes</legend>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {ALLOWED_SCOPES.map((scope) => (
            <label key={scope} className="flex items-start gap-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-neutral-300"
                checked={scopes.includes(scope)}
                onChange={() => toggleScope(scope)}
              />
              <code className="font-mono text-xs">{scope}</code>
            </label>
          ))}
        </div>
      </fieldset>
      <Input
        label="Expires in (days)"
        type="number"
        min={1}
        max={730}
        placeholder="Never"
        value={expiresIn}
        onChange={(e) => setExpiresIn(e.target.value)}
      />
      {error !== null && (
        <p
          role="alert"
          className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
        >
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" loading={mutation.isPending}>
          Create key
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function CreatedKeyBanner({
  created,
  onClose,
}: {
  created: CreatedApiKey;
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-success-200 bg-success-50 p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-success-700">Key created — copy it now</h2>
      <p className="mt-1 text-sm text-success-700">
        This is the only time we&apos;ll show the secret. Store it safely.
      </p>
      <pre className="mt-4 overflow-x-auto rounded border border-success-300 bg-white p-3 font-mono text-xs text-neutral-900">
        {created.plain_token}
      </pre>
      <div className="mt-4 flex gap-2">
        <Button
          variant="primary"
          onClick={() => {
            void navigator.clipboard.writeText(created.plain_token);
          }}
        >
          Copy to clipboard
        </Button>
        <Button variant="outline" onClick={onClose}>
          I&apos;ve saved it
        </Button>
      </div>
    </div>
  );
}
